import { Copy, CreditCard, DollarSign, Banknote, Coins } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Modal } from "@/shared/components/Modal";
import type { PaymentsTranslations } from "@/shared/lang/interfaces/payments";
import useTranslations from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

import { isCryptoWalletCurrency } from "../../confirmations/constants/currencies.config";
import { FORM_METHOD_FIELD_LABEL_TKEYS } from "../constants/form-method-field-translations";
import { formatPaymentAmount } from "../utils/formatPaymentAmount";

const CURRENCY_ICON: Record<string, LucideIcon> = {
  USD: DollarSign,
  COP: Banknote,
  EUR: Banknote,
  USDT: Coins,
  USDC: Coins,
};

function formatKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^\s+/, "")
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function getFieldLabel(key: string, t: (k: keyof PaymentsTranslations) => string): string {
  const translationKey = FORM_METHOD_FIELD_LABEL_TKEYS[key.trim()];
  if (translationKey) return t(translationKey);
  return formatKey(key);
}

function formatValue(value: unknown, options?: { yes: string; no: string }): string {
  if (value == null) return "—";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? (options?.yes ?? "Sí") : (options?.no ?? "No");
  return JSON.stringify(value);
}

async function copyToClipboard(text: string): Promise<boolean> {
  if (!text || text === "—") return false;
  try {
    await navigator.clipboard.writeText(text);
    colocaToast.success("Copiado");
    return true;
  } catch {
    return false;
  }
}

function normalizeAmountForCopy(formatted: string): string {
  return formatted.replace(/\./g, "").replace(",", ".");
}

type PaymentInfoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  paymentName: string;
  amount?: string;
  currency?: string;
  bankMetadata: Record<string, unknown> | null | undefined;
  bank?: string | null;
  onConfirmPayment?: () => void;
};

export const PaymentInfoModal = ({
  isOpen,
  onClose,
  paymentName,
  amount,
  currency,
  bankMetadata,
  bank,
  onConfirmPayment,
}: PaymentInfoModalProps) => {
  const { t } = useTranslations("payments");
  const amountIcon: LucideIcon = (currency && CURRENCY_ICON[currency.toUpperCase()]) || CreditCard;
  const entries =
    bankMetadata != null && typeof bankMetadata === "object"
      ? Object.entries(bankMetadata).filter(([, v]) => v !== undefined && v !== null && v !== "")
      : [];

  const hasDireccionWallet =
    bankMetadata != null &&
    typeof bankMetadata === "object" &&
    "direccionWallet" in bankMetadata &&
    bankMetadata.direccionWallet !== undefined &&
    bankMetadata.direccionWallet !== null &&
    String(bankMetadata.direccionWallet).trim() !== "";
  const isStablecoinCrypto = currency != null && isCryptoWalletCurrency(String(currency));
  const hasBank = bank != null && String(bank).trim() !== "";
  const showNetwork = isStablecoinCrypto && hasDireccionWallet && hasBank;

  const yesNo = { yes: t("modal_info_yes"), no: t("modal_info_no") };

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        className="min-w-[100px] h-11 px-5 rounded-full bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-slate-200 text-sm font-semibold transition-all hover:bg-slate-200 dark:hover:bg-zinc-700"
      >
        {t("modal_info_close")}
      </button>
      <button
        type="button"
        onClick={() => onConfirmPayment?.()}
        className="min-w-[140px] h-11 px-5 rounded-full bg-green-500 text-white text-sm font-semibold transition-all hover:brightness-110 shadow-lg shadow-green-500/20"
      >
        {t("modal_info_confirm_payment")}
      </button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("modal_info_title")} size="md" footer={footer}>
      {entries.length === 0 && !amount && !currency && !paymentName ? (
        <p className="text-slate-500 dark:text-slate-400 text-sm">{t("modal_info_no_data")}</p>
      ) : (
        <div className="space-y-1">
          {paymentName && (
            <p className="text-sm text-slate-700 dark:text-slate-300 mb-3">
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                {t("card_beneficiary")}{" "}
              </span>
              <span className="font-medium">{paymentName}</span>
            </p>
          )}
          {(amount != null || currency != null) &&
            (() => {
              const displayAmount =
                currency && amount != null
                  ? `${currency} ${formatPaymentAmount(amount)}`
                  : amount != null
                    ? formatPaymentAmount(amount)
                    : (currency ?? "—");
              const amountToCopy = amount != null ? normalizeAmountForCopy(formatPaymentAmount(amount)) : "—";
              return (
                <div className="rounded-lg bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-4 mb-4">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
                    {t("modal_info_amount_label")}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex shrink-0 items-center justify-center w-10 h-10 rounded-lg bg-emerald-500 text-emerald-100">
                      {(() => {
                        const Icon = amountIcon;
                        return <Icon className="h-5 w-5" />;
                      })()}
                    </div>
                    <span className="text-lg font-bold text-slate-800 dark:text-slate-200 flex-1 min-w-0">
                      {displayAmount}
                    </span>
                    <button
                      type="button"
                      onClick={() => void copyToClipboard(amountToCopy)}
                      className="flex shrink-0 items-center justify-center w-9 h-9 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                      title={t("modal_info_copy_amount_title")}
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })()}
          {showNetwork && (
            <div className="flex flex-col gap-0.5 py-2 border-b border-slate-100 dark:border-slate-800 mb-1">
              <dt className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                {t("card_network_label")}
              </dt>
              <dd className="text-sm text-slate-900 dark:text-white break-all">
                <button
                  type="button"
                  onClick={() => void copyToClipboard(String(bank))}
                  className="inline-flex items-center gap-2 w-full text-left rounded-md py-1.5 px-2 -mx-2 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors cursor-pointer group"
                  title={t("modal_info_copy_title")}
                >
                  <span className="flex-1 min-w-0 break-all">{bank}</span>
                  <Copy className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 shrink-0" />
                </button>
              </dd>
            </div>
          )}
          <dl className="space-y-3">
            {entries.map(([key, value]) => {
              const displayValue = formatValue(value, yesNo);
              return (
                <div
                  key={key}
                  className="flex flex-col gap-0.5 py-2 border-b border-slate-100 dark:border-slate-800 last:border-0"
                >
                  <dt className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                    {getFieldLabel(key, t)}
                  </dt>
                  <dd className="text-sm text-slate-900 dark:text-white break-all">
                    <button
                      type="button"
                      onClick={() => void copyToClipboard(displayValue)}
                      className="inline-flex items-center gap-2 w-full text-left rounded-md py-1.5 px-2 -mx-2 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors cursor-pointer group"
                      title={t("modal_info_copy_title")}
                    >
                      <span className="flex-1 min-w-0 break-all">{displayValue}</span>
                      <Copy className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 shrink-0" />
                    </button>
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      )}
    </Modal>
  );
};
